import { useState, type KeyboardEvent } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    Cell,
} from "recharts";
import { ClaimStatus } from "@/types/workspace";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useClaimPipelines } from "@/hooks/use-claim-pipelines";
import { useSharedClaimFilterOptions } from "@/hooks/use-claims";

const enhancedColors = [
    "#94a3b8", // slate-400
    "#86a789", // sage green
    "#dbb9a4", // sand
    "#a8a29e", // stone-400
    "#9ca3af", // gray-400
    "#c7b7c7", // mauve
    "#a3b8cc", // steel blue
    "#d4c4b8", // taupe
];

interface ClaimsStatusChartProps {
    claimsByStatus: ClaimStatus[];
}

function shortenStatusLabel(status: string, maxLength: number): string {
    return status.length > maxLength ? `${status.slice(0, maxLength - 3)}...` : status;
}

export function ClaimsStatusChart({ claimsByStatus }: ClaimsStatusChartProps) {
    const [, navigate] = useLocation();
    const { pipelines, isLoading: isLoadingPipelines, error: pipelinesError } = useClaimPipelines({ includeInactive: true });
    const { pipelines: sharedPipelines, isLoading: isLoadingSharedPipelines, error: sharedPipelinesError } = useSharedClaimFilterOptions();
    const isLoading = isLoadingPipelines || isLoadingSharedPipelines;
    const error = pipelinesError || sharedPipelinesError;
    const [selectedPipelineId, setSelectedPipelineId] = useState<string>();
    const availablePipelines = [
        ...pipelines,
        ...sharedPipelines
            .filter((pipeline) => !pipelines.some((existing) => existing.id === pipeline.id))
            .map((pipeline) => ({ ...pipeline, isActive: true, isSystemDefault: false })),
    ].filter((pipeline) =>
        pipeline.isActive || claimsByStatus.some((item) => item.pipelineId === pipeline.id && item.count > 0)
    );
    const selectedPipeline = availablePipelines.find((pipeline) => String(pipeline.id) === selectedPipelineId)
        ?? availablePipelines.find((pipeline) => pipeline.isSystemDefault && pipeline.isActive)
        ?? availablePipelines.find((pipeline) => pipeline.isActive)
        ?? availablePipelines[0];
    const selectedValue = selectedPipeline ? String(selectedPipeline.id) : "";
    // Vertical bars read well on wide screens, but angled category labels crowd on
    // narrow ones. Switch to horizontal bars on mobile (< md) so every status label
    // stays upright and legible. Desktop (>= md) renders the original chart unchanged.
    const isDesktop = useMediaQuery("(min-width: 768px)");
    const pieData = claimsByStatus
        .filter((item) => item.pipelineId === selectedPipeline?.id && item.count > 0)
        .map((item, index) => ({
            ...item,
            color: item.color || enhancedColors[index % enhancedColors.length],
        }));

    const openStatusClaims = (status: ClaimStatus) => {
        if (!Number.isSafeInteger(status.statusTemplateId) || status.statusTemplateId <= 0) return;
        const params = new URLSearchParams({
            pipelineId: String(status.pipelineId),
            claimStatusTemplateId: String(status.statusTemplateId),
        });
        navigate(`/claims?${params.toString()}`);
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="h-full min-w-0"
        >
            <Tabs value={selectedValue} onValueChange={setSelectedPipelineId} asChild>
            <Card className="h-[480px] min-w-0 flex flex-col overflow-hidden">
                <CardHeader className="min-w-0 shrink-0 pb-4 [overflow-wrap:anywhere]">
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <div className="p-2 bg-muted rounded-lg">
                            <FileText className="h-5 w-5 text-muted-foreground" />
                        </div>
                        Claims by Status
                    </CardTitle>
                    <CardDescription>
                        Distribution of your claims across all statuses
                    </CardDescription>
                </CardHeader>
                <TabsContent value={selectedValue} className="mt-0 min-h-0 flex-1" asChild>
                <CardContent className="min-w-0 overflow-hidden pb-6">
                    {isLoading || error ? (
                        <div className="flex items-center justify-center h-full" role="status">
                            <p className="text-muted-foreground">{isLoading ? "Loading workflows..." : "Unable to load workflows"}</p>
                        </div>
                    ) : pieData.length > 0 ? (
                        <div className="h-full w-full min-w-0 overflow-hidden">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={pieData}
                                    layout={isDesktop ? "horizontal" : "vertical"}
                                    margin={
                                        isDesktop
                                            ? { top: 24, right: 20, left: 0, bottom: 12 }
                                            : { top: 10, right: 16, left: 4, bottom: 10 }
                                    }
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            horizontal={isDesktop}
                                            vertical={!isDesktop}
                                            className="stroke-border"
                                        />
                                        {isDesktop ? (
                                            <>
                                                <XAxis
                                                    dataKey="status"
                                                    tickFormatter={(status: string) => shortenStatusLabel(status, 12)}
                                                    angle={-45}
                                                    textAnchor="end"
                                                    height={100}
                                                    interval={0}
                                                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    width={40}
                                                    allowDecimals={false}
                                                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <XAxis
                                                    type="number"
                                                    allowDecimals={false}
                                                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    type="category"
                                                    dataKey="status"
                                                    tickFormatter={(status: string) => shortenStatusLabel(status, 8)}
                                                    width={104}
                                                    interval={0}
                                                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                />
                                            </>
                                        )}
                                        <Tooltip
                                            wrapperStyle={{ maxWidth: "100%" }}
                                            contentStyle={{
                                                backgroundColor: "hsl(var(--card))",
                                                border: "1px solid hsl(var(--border))",
                                                borderRadius: "8px",
                                                maxWidth: 240,
                                                overflowWrap: "anywhere",
                                                whiteSpace: "normal",
                                            }}
                                            labelStyle={{
                                                color: "hsl(var(--foreground))",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                overflowWrap: "anywhere",
                                            }}
                                            itemStyle={{ color: "hsl(var(--foreground))" }}
                                            cursor={{ fill: "transparent" }}
                                        />
                                        <Bar
                                            dataKey="count"
                                            className="cursor-pointer"
                                            animationBegin={0}
                                            animationDuration={100}
                                            radius={isDesktop ? [8, 8, 0, 0] : [0, 8, 8, 0]}
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={entry.color}
                                                    tabIndex={0}
                                                    role="link"
                                                    aria-label={`View ${entry.status} claims in ${selectedPipeline?.name}`}
                                                    onClick={() => openStatusClaims(entry)}
                                                    onKeyDown={(event: KeyboardEvent) => {
                                                        if (event.key === "Enter" || event.key === " ") {
                                                            event.preventDefault();
                                                            openStatusClaims(entry);
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <p className="text-muted-foreground">{selectedPipeline ? "No claims in this workflow" : "No claims yet"}</p>
                        </div>
                    )}
                </CardContent>
                </TabsContent>
                {!isLoading && !error && availablePipelines.length > 0 && (
                    <div className="mx-6 mb-6 min-w-0 shrink-0 overflow-x-auto pb-1">
                        <TabsList aria-label="Claim workflow" className="w-max justify-start">
                            {availablePipelines.map((pipeline) => (
                                <TabsTrigger key={pipeline.id} value={String(pipeline.id)} className="max-w-48 gap-2" title={pipeline.name}>
                                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: pipeline.color ?? enhancedColors[0] }} aria-hidden="true" />
                                    <span className="min-w-0 truncate">{pipeline.name}</span>
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </div>
                )}
            </Card>
            </Tabs>
        </motion.div>
    );
}
